import { create } from 'zustand';
import toast from 'react-hot-toast';

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  flag: string;
}

export const CURRENCIES: Currency[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', flag: '🇳🇬' },
  { code: 'GHS', symbol: '₵', name: 'Ghanaian Cedi', flag: '🇬🇭' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', flag: '🇰🇪' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', flag: '🇿🇦' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', flag: '🇦🇪' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc', flag: '🇨🇭' },
];

// Paystack supported African currencies
export const PAYSTACK_CURRENCIES = ['NGN', 'GHS', 'KES', 'ZAR', 'USD'];

// PayPal supported currencies
export const PAYPAL_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF',
];

// Tells the customer what payment options apply once their currency is
// set, so there are no surprises at checkout.
const showCurrencyPaymentNotice = (currency: Currency, { once }: { once: boolean }) => {
  if (typeof window === 'undefined') return;
  if (once) {
    if (sessionStorage.getItem('ls_currency_notice_shown')) return;
    sessionStorage.setItem('ls_currency_notice_shown', 'true');
  }
  const paymentInfo = currency.code === 'NGN'
    ? 'you can pay via Paystack (card, bank transfer, USSD) or Card at checkout.'
    : 'you can pay via Card at checkout.';
  toast(`Shopping in ${currency.name} (${currency.symbol}) — ${paymentInfo}`, {
    duration: 6000,
    position: 'bottom-center',
    icon: currency.flag,
  });
};

interface CurrencyState {
  selectedCurrency: Currency;
  exchangeRates: Record<string, number>;
  isLoading: boolean;
  setCurrency: (currency: Currency) => void;
  fetchExchangeRates: () => Promise<void>;
  convert: (amount: number, fromCurrency?: string) => number;
  format: (amount: number, fromCurrency?: string) => string;
}

export const useCurrencyStore = create<CurrencyState>((set, get) => ({
  selectedCurrency: CURRENCIES[0],
  exchangeRates: { USD: 1 },
  isLoading: false,

  setCurrency: (currency) => {
    localStorage.setItem('lanre_savage_currency', JSON.stringify(currency));
    set({ selectedCurrency: currency });
    showCurrencyPaymentNotice(currency, { once: false });
  },

  fetchExchangeRates: async () => {
    try {
      set({ isLoading: true });
      const stored = localStorage.getItem('lanre_savage_currency');
      if (stored) {
        const parsed = JSON.parse(stored);
        const found = CURRENCIES.find((c) => c.code === parsed.code);
        if (found) set({ selectedCurrency: found });
      }

      // Try to detect user location
try {
  const geoRes = await fetch('https://ipapi.co/json/', {
    signal: AbortSignal.timeout(3000),
  });
  if (geoRes.ok) {
    const geoData = await geoRes.json();
    const countryCurrencyMap: Record<string, string> = {
      NG: 'NGN', GH: 'GHS', KE: 'KES', ZA: 'ZAR',
      GB: 'GBP', DE: 'EUR', FR: 'EUR', IT: 'EUR',
      CA: 'CAD', AU: 'AUD', JP: 'JPY', AE: 'AED',
      CH: 'CHF', US: 'USD',
    };
    const detectedCode = countryCurrencyMap[geoData.country_code];
    if (detectedCode && !stored) {
      const detected = CURRENCIES.find((c) => c.code === detectedCode);
      if (detected) set({ selectedCurrency: detected });
    }
  }
} catch {}

      // Let the customer know what payment options apply for whatever
      // currency ended up selected (auto-detected or previously chosen) —
      // shown at most once per browser session so it's not naggy on
      // every page load.
      showCurrencyPaymentNotice(get().selectedCurrency, { once: true });

      // Fetch exchange rates
      const res = await fetch(
        'https://api.exchangerate-api.com/v4/latest/USD'
      );
      const data = await res.json();
      if (data.rates) {
        set({ exchangeRates: data.rates });
      }
    } catch {
      // Fallback rates if API fails
      set({
        exchangeRates: {
          USD: 1, EUR: 0.92, GBP: 0.79, NGN: 1580,
          GHS: 15.5, KES: 129, ZAR: 18.5, CAD: 1.36,
          AUD: 1.53, JPY: 149, AED: 3.67, CHF: 0.90,
        },
      });
    } finally {
      set({ isLoading: false });
    }
  },

  convert: (amount, fromCurrency = 'USD') => {
    const { selectedCurrency, exchangeRates } = get();
    if (fromCurrency === selectedCurrency.code) return amount;
    const fromRate = exchangeRates[fromCurrency] || 1;
    const toRate = exchangeRates[selectedCurrency.code] || 1;
    return (amount / fromRate) * toRate;
  },

  format: (amount, fromCurrency = 'USD') => {
    const { selectedCurrency, convert } = get();
    const converted = convert(amount, fromCurrency);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCurrency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(converted);
  },
}));